<template>
  <v-card
      outlined
      class="anexo-item-card mb-4"
      :class="{ 'error-state': temErro, 'success-state': anexoCompleto }"
  >
    <!-- Header do Card -->
    <v-card-title class="anexo-header pa-3">
      <div class="d-flex align-center flex-grow-1">
        <v-chip
            small
            :color="anexoCompleto ? '#023449' : '#64748b'"
            dark
            class="mr-3 font-weight-bold"
        >
          Anexo {{ anexoInterno.identificador }}
        </v-chip>

        <div class="flex-grow-1">
          <div v-if="anexoInterno.titulo" class="anexo-header-title">
            {{ anexoInterno.titulo }}
          </div>
          <div v-else class="anexo-header-placeholder">
            Configure o anexo {{ anexoInterno.identificador }}
          </div>
        </div>

        <!-- Badge de Status -->
        <v-chip
            v-if="anexoCompleto"
            x-small
            color="success"
            dark
            class="mr-2"
        >
          <v-icon left x-small>mdi-check-circle</v-icon>
          Completo
        </v-chip>

        <!-- Botão Remover -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
                icon
                small
                color="error"
                @click="confirmarRemocao"
                :disabled="disabled"
                v-bind="attrs"
                v-on="on"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>Remover anexo</span>
        </v-tooltip>
      </div>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4">
      <!-- Grid de Configuração -->
      <v-row>
        <!-- Identificador -->
        <v-col cols="12" sm="3" md="2">
          <div class="field-label mb-2">
            <v-icon x-small class="mr-1">mdi-tag</v-icon>
            Identificador
          </div>
          <v-text-field
              v-model="anexoInterno.identificador"
              placeholder="Ex: A"
              outlined
              dense
              :rules="regras.identificador"
              :disabled="disabled"
              maxlength="10"
              hide-details="auto"
              @input="emitirAlteracao"
              class="compact-field"
          />
        </v-col>

        <!-- Tipo de Arquivo -->
        <v-col cols="12" sm="3" md="2">
          <div class="field-label mb-2">
            <v-icon x-small class="mr-1">mdi-file</v-icon>
            Tipo
          </div>
          <v-select
              v-model="anexoInterno.arquivo_tipo"
              :items="tiposArquivo"
              outlined
              dense
              :rules="regras.tipo"
              :disabled="disabled || !!anexoInterno.arquivo_base64"
              hide-details="auto"
              @change="atualizarTipoArquivo"
              class="compact-field"
          >
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center">
                <v-icon small :color="getCorTipo(item)" class="mr-2">
                  {{ getIconeTipo(item) }}
                </v-icon>
                <span class="text-uppercase font-weight-medium">{{ item }}</span>
              </div>
            </template>
            <template v-slot:item="{ item }">
              <v-icon small :color="getCorTipo(item)" class="mr-2">
                {{ getIconeTipo(item) }}
              </v-icon>
              <span class="text-uppercase">{{ item }}</span>
            </template>
          </v-select>
        </v-col>

        <!-- Título -->
        <v-col cols="12" sm="6" md="8">
          <div class="field-label mb-2">
            <v-icon x-small class="mr-1">mdi-format-title</v-icon>
            Título do Anexo
          </div>
          <v-text-field
              v-model="anexoInterno.titulo"
              placeholder="Ex: Checklist de Inspeção"
              outlined
              dense
              :rules="regras.titulo"
              :disabled="disabled"
              :error="!nomeArquivoValido && !!anexoInterno.titulo"
              :success="nomeArquivoValido && !!anexoInterno.titulo"
              maxlength="200"
              counter
              hide-details="auto"
              @input="atualizarTitulo"
              class="compact-field"
          >
            <template v-slot:append>
              <v-tooltip bottom max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      :color="nomeArquivoValido ? 'success' : 'grey'"
                  >
                    {{ nomeArquivoValido ? 'mdi-check-circle' : 'mdi-information' }}
                  </v-icon>
                </template>
                <span>
                  Use apenas letras, números, espaços, hífen e underscore.
                  <br><strong>Nome que será salvo:</strong> {{ nomeArquivoGerado }}
                </span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
      </v-row>

      <!-- Área de Upload/Preview do Arquivo -->
      <v-row class="mt-2">
        <v-col cols="12">
          <!-- Estado: SEM arquivo -->
          <div v-if="!anexoInterno.arquivo_base64" class="upload-zone">
            <v-card
                outlined
                class="upload-card-direct"
                :class="{ 'upload-disabled': disabled || !nomeArquivoValido, 'drag-over': isDragging }"
                @dragover.prevent="handleDragOver"
                @dragleave.prevent="handleDragLeave"
                @drop.prevent="handleDrop"
            >
              <v-card-text class="text-center pa-6">
                <v-icon size="56" :color="isDragging ? 'primary' : (nomeArquivoValido ? '#64748b' : '#cbd5e1')">
                  {{ isDragging ? 'mdi-download' : 'mdi-cloud-upload' }}
                </v-icon>
                <p class="mt-4 mb-2 text-subtitle-1 font-weight-medium">
                  {{ isDragging ? 'Solte o arquivo aqui' : (nomeArquivoValido ? 'Arraste o arquivo ou clique para selecionar' : 'Configure o título primeiro') }}
                </p>
                <p class="text-caption grey--text mb-4">
                  Arquivo: {{ nomeArquivoGerado }}
                  <br>
                  Tamanho máximo: 20MB
                </p>
                <v-btn
                    color="primary"
                    outlined
                    :disabled="disabled || !nomeArquivoValido"
                    @click.stop="openFileDialog"
                >
                  <v-icon left>mdi-file-search</v-icon>
                  Selecionar Arquivo
                </v-btn>

                <input
                    ref="fileInput"
                    type="file"
                    :accept="acceptTypes"
                    @change="handleFileSelect"
                    style="display: none"
                />
              </v-card-text>

              <!-- Loading Overlay -->
              <v-overlay v-if="uploading" absolute>
                <v-progress-circular
                    indeterminate
                    color="primary"
                    size="48"
                ></v-progress-circular>
                <p class="mt-3 white--text">Processando arquivo...</p>
              </v-overlay>
            </v-card>
          </div>

          <!-- Estado: COM arquivo -->
          <div v-else class="file-preview-zone">
            <v-card
                outlined
                hover
                class="file-preview-card"
                @click="handleArquivoClick"
            >
              <v-card-text class="pa-4">
                <div class="d-flex align-center">
                  <!-- Ícone do arquivo -->
                  <div class="file-icon-container mr-4">
                    <v-icon :color="getCorTipo(anexoInterno.arquivo_tipo)" size="48">
                      {{ getIconeTipo(anexoInterno.arquivo_tipo) }}
                    </v-icon>
                  </div>

                  <!-- Informações do arquivo -->
                  <div class="flex-grow-1">
                    <div class="file-preview-name">{{ nomeArquivoGerado }}</div>
                    <div class="d-flex align-center mt-2" style="gap: 8px;">
                      <span class="text-caption grey--text">
                        {{ formatarTamanho(anexoInterno.tamanho_bytes) }}
                      </span>
                      <span class="text-caption grey--text">•</span>
                      <span class="text-caption grey--text text-uppercase">
                        {{ anexoInterno.arquivo_tipo }}
                      </span>
                      <v-chip
                          v-if="isPDF"
                          x-small
                          color="#e3f2fd"
                          text-color="#1565c0"
                      >
                        <v-icon left x-small>mdi-eye</v-icon>
                        Clique para visualizar
                      </v-chip>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </v-col>
      </v-row>

      <!-- Mensagens de Erro -->
      <v-row v-if="erros.length > 0" class="mt-2">
        <v-col cols="12">
          <v-alert
              v-for="(erro, index) in erros"
              :key="index"
              type="error"
              dense
              text
              class="mb-2"
              border="left"
          >
            <v-icon left small>mdi-alert-circle</v-icon>
            {{ erro }}
          </v-alert>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Dialog de Visualização de PDF -->
    <v-dialog
        v-model="dialogVisualizacao"
        max-width="1400px"
        @click:outside="fecharVisualizacao"
    >
      <v-card v-if="pdfParaVisualizar">
        <v-card-title class="pdf-viewer-header pa-4 white--text">
          <v-icon :color="getCorTipo(anexoInterno.arquivo_tipo)" class="mr-2">
            {{ getIconeTipo(anexoInterno.arquivo_tipo) }}
          </v-icon>
          <div class="flex-grow-1">
            <div class="text-subtitle-1 font-weight-medium">{{ nomeArquivoGerado }}</div>
            <div class="text-caption grey--text">Anexo {{ anexoInterno.identificador }}</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn
              icon
              @click="fecharVisualizacao"
              color="white"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <div v-if="carregandoPDF" class="pdf-loading-container">
            <v-progress-circular
                indeterminate
                color="primary"
                size="64"
            ></v-progress-circular>
            <p class="mt-4 text-subtitle-1">Carregando preview...</p>
          </div>
          <iframe
              v-else
              :src="pdfParaVisualizar"
              class="pdf-viewer-iframe"
              frameborder="0"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'NormativaAnexoItem',

  props: {
    value: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      anexoInterno: {
        identificador: '',
        titulo: '',
        arquivo_nome: '',
        arquivo_base64: '',
        arquivo_tipo: 'pdf',
        tamanho_bytes: 0
      },

      dialogVisualizacao: false,
      pdfParaVisualizar: null,
      carregandoPDF: false,
      uploading: false,
      isDragging: false,
      erros: [],

      tiposArquivo: ['pdf', 'docx', 'doc', 'xlsx', 'xls'],

      regras: {
        identificador: [
          v => !!v || 'Identificador é obrigatório',
          v => (v && v.trim().length > 0) || 'Identificador não pode ser vazio'
        ],
        titulo: [
          v => !!v || 'Título é obrigatório',
          v => (v && v.trim().length > 0) || 'Título não pode ser vazio',
          v => (v && v.length <= 200) || 'Título deve ter no máximo 200 caracteres'
        ],
        tipo: [
          v => !!v || 'Tipo é obrigatório'
        ]
      }
    }
  },

  computed: {
    isPDF() {
      return this.anexoInterno.arquivo_tipo === 'pdf'
    },

    anexoCompleto() {
      return this.anexoInterno.identificador &&
          this.anexoInterno.titulo &&
          this.anexoInterno.arquivo_base64 &&
          this.nomeArquivoValido
    },

    temErro() {
      return this.erros.length > 0 || (!!this.anexoInterno.titulo && !this.nomeArquivoValido)
    },

    nomeArquivoGerado() {
      if (!this.anexoInterno.titulo || !this.anexoInterno.titulo.trim()) {
        return `anexo-${this.anexoInterno.identificador || 'x'}.${this.anexoInterno.arquivo_tipo || 'pdf'}`
      }

      const extensao = this.anexoInterno.arquivo_tipo || 'pdf'
      let nomeArquivo = this.anexoInterno.titulo.trim()

      nomeArquivo = nomeArquivo
          .replace(/[^\w\sÀ-ÿ-]/g, '')
          .replace(/\s+/g, ' ')
          .trim()

      return `${nomeArquivo}.${extensao}`
    },

    nomeArquivoValido() {
      if (!this.anexoInterno.titulo || !this.anexoInterno.titulo.trim()) {
        return false
      }

      const nomeArquivo = this.nomeArquivoGerado
      const partes = nomeArquivo.split('.')
      if (partes.length < 2) return false

      const extensao = partes.pop().toLowerCase()
      const nomeBase = partes.join('.')

      const extensoesPermitidas = ['pdf', 'xlsx', 'xls', 'docx', 'doc']
      if (!extensoesPermitidas.includes(extensao)) {
        return false
      }

      const regexValido = /^[a-zA-Z0-9\u00C0-\u00FF _-]+$/

      return regexValido.test(nomeBase) && nomeBase.length >= 3
    },

    acceptTypes() {
      const mimeTypes = {
        'pdf': 'application/pdf',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }

      if (this.anexoInterno.arquivo_tipo) {
        return mimeTypes[this.anexoInterno.arquivo_tipo] || ''
      }

      return Object.values(mimeTypes).join(',')
    }
  },

  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.anexoInterno = { ...newValue }
        }
      }
    }
  },

  methods: {
    // Drag and Drop handlers
    handleDragOver() {
      if (!this.disabled && this.nomeArquivoValido) {
        this.isDragging = true
      }
    },

    handleDragLeave() {
      this.isDragging = false
    },

    handleDrop(event) {
      this.isDragging = false
      if (this.disabled || !this.nomeArquivoValido) return

      const files = event.dataTransfer.files
      if (files.length > 0) {
        this.processFile(files[0])
      }
    },

    // File selection handlers
    openFileDialog() {
      if (!this.disabled && this.nomeArquivoValido) {
        this.$refs.fileInput.click()
      }
    },

    handleFileSelect(event) {
      const files = event.target.files
      if (files.length > 0) {
        this.processFile(files[0])
      }
      // Limpar input para permitir selecionar o mesmo arquivo novamente
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    async processFile(file) {
      this.uploading = true
      this.erros = []

      try {
        // Validar extensão
        const extensao = this.getFileExtension(file.name)
        if (!this.tiposArquivo.includes(extensao)) {
          throw new Error(`Tipo de arquivo não permitido. Use: ${this.tiposArquivo.join(', ').toUpperCase()}`)
        }

        // Validar se o tipo corresponde ao selecionado
        if (this.anexoInterno.arquivo_tipo && extensao !== this.anexoInterno.arquivo_tipo) {
          throw new Error(`O arquivo deve ser do tipo ${this.anexoInterno.arquivo_tipo.toUpperCase()}`)
        }

        // Validar tamanho (20MB)
        const tamanhoMaximoBytes = 20 * 1024 * 1024
        if (file.size > tamanhoMaximoBytes) {
          throw new Error('Arquivo muito grande. Tamanho máximo: 20MB')
        }

        // Converter para base64
        const base64 = await this.fileToBase64(file)

        // Atualizar dados
        this.anexoInterno.arquivo_nome = this.nomeArquivoGerado
        this.anexoInterno.arquivo_base64 = base64
        this.anexoInterno.tamanho_bytes = file.size

        if (!this.anexoInterno.arquivo_tipo) {
          this.anexoInterno.arquivo_tipo = extensao
        }

        this.emitirAlteracao()

      } catch (error) {
        this.erros.push(error.message)
      } finally {
        this.uploading = false
      }
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = () => {
          const base64 = reader.result.split(',')[1]
          resolve(base64)
        }

        reader.onerror = () => {
          reject(new Error('Erro ao ler o arquivo'))
        }

        reader.readAsDataURL(file)
      })
    },

    getFileExtension(fileName) {
      return fileName.split('.').pop().toLowerCase()
    },

    // Handlers de ações
    handleArquivoClick() {
      if (this.isPDF) {
        this.visualizarArquivo()
      }
    },

    visualizarArquivo() {
      if (!this.anexoInterno.arquivo_base64 || !this.isPDF) return

      this.carregandoPDF = true
      this.dialogVisualizacao = true

      try {
        const pdfParams = [
          'navpanes=1',
          'pagemode=thumbs',
          'page=1'
        ].join('&')

        this.pdfParaVisualizar = `data:application/pdf;base64,${this.anexoInterno.arquivo_base64}#${pdfParams}`
      } catch (error) {
        console.error('Erro ao gerar preview:', error)
        this.erros.push('Erro ao visualizar PDF')
      } finally {
        this.carregandoPDF = false
      }
    },

    fecharVisualizacao() {
      this.dialogVisualizacao = false
      this.pdfParaVisualizar = null
    },

    confirmarRemocaoArquivo() {
      if (confirm('Tem certeza que deseja remover este arquivo?')) {
        this.anexoInterno.arquivo_base64 = ''
        this.anexoInterno.tamanho_bytes = 0
        this.anexoInterno.arquivo_nome = ''
        this.emitirAlteracao()
      }
    },

    confirmarRemocao() {
      if (confirm(`Tem certeza que deseja remover o Anexo ${this.anexoInterno.identificador}?`)) {
        this.$emit('remover', this.index)
      }
    },

    // Métodos auxiliares
    atualizarTitulo() {
      this.anexoInterno.arquivo_nome = this.nomeArquivoGerado
      this.$emit('atualizar-nome', {
        index: this.index,
        titulo: this.anexoInterno.titulo,
        extensao: this.anexoInterno.arquivo_tipo || 'pdf'
      })
      this.emitirAlteracao()
    },

    atualizarTipoArquivo() {
      this.anexoInterno.arquivo_nome = this.nomeArquivoGerado
      this.emitirAlteracao()
    },

    emitirAlteracao() {
      this.validar()
      this.$emit('input', this.anexoInterno)
      this.$emit('change', this.anexoInterno)
    },

    validar() {
      this.erros = []

      if (!this.anexoInterno.identificador || this.anexoInterno.identificador.trim() === '') {
        this.erros.push('Identificador é obrigatório')
      }

      if (!this.anexoInterno.titulo || this.anexoInterno.titulo.trim() === '') {
        this.erros.push('Título é obrigatório')
      }

      if (this.anexoInterno.titulo && !this.nomeArquivoValido) {
        this.erros.push('Título contém caracteres inválidos ou é muito curto (mínimo 3 caracteres)')
      }

      if (!this.anexoInterno.arquivo_tipo) {
        this.erros.push('Tipo de arquivo é obrigatório')
      }

      if (!this.anexoInterno.arquivo_base64) {
        this.erros.push('Upload do arquivo é obrigatório')
      }

      return this.erros.length === 0
    },

    getIconeTipo(tipo) {
      const icones = {
        'pdf': 'mdi-file-pdf-box',
        'doc': 'mdi-file-word-box',
        'docx': 'mdi-file-word-box',
        'xls': 'mdi-file-excel-box',
        'xlsx': 'mdi-file-excel-box'
      }
      return icones[tipo] || 'mdi-file-document'
    },

    getCorTipo(tipo) {
      const cores = {
        'pdf': 'red',
        'doc': 'blue',
        'docx': 'blue',
        'xls': 'green',
        'xlsx': 'green'
      }
      return cores[tipo] || 'grey'
    },

    formatarTamanho(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
/* Card Principal */
.anexo-item-card {
  transition: all 0.3s ease;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
}

.anexo-item-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.anexo-item-card.error-state {
  border-color: #f44336;
  background-color: #ffebee;
}

.anexo-item-card.success-state {
  border-color: #4caf50;
  background-color: #f1f8f4;
}

/* Header */
.anexo-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 1px solid #e2e8f0;
}

.anexo-header-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.anexo-header-placeholder {
  font-size: 0.938rem;
  color: #94a3b8;
  font-style: italic;
}

/* Fields */
.field-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
}

.compact-field ::v-deep .v-input__control {
  min-height: 40px !important;
}

/* Upload Zone - Drag and Drop */
.upload-zone {
  margin-top: 8px;
}

.upload-card-direct {
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafafa;
  position: relative;
}

.upload-card-direct:hover:not(.upload-disabled) {
  border-color: #023449;
  background: #f0f9ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(2, 52, 73, 0.1);
}

.upload-card-direct.drag-over {
  border-color: #023449;
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  transform: scale(1.02);
  box-shadow: 0 8px 24px rgba(2, 52, 73, 0.15);
}

.upload-card-direct.upload-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* File Preview */
.file-preview-card {
  border-radius: 12px;
  border: 2px solid #e8f5e9;
  background: linear-gradient(135deg, #ffffff 0%, #f1f8f4 100%);
  cursor: pointer;
  transition: all 0.3s ease;
}

.file-preview-card:hover {
  border-color: #4caf50;
  box-shadow: 0 4px 16px rgba(76, 175, 80, 0.15);
  transform: translateY(-2px);
}

.file-icon-container {
  background: #f5f5f5;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-preview-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  word-break: break-word;
}

/* PDF Viewer */
.pdf-viewer-header {
  background: linear-gradient(135deg, #023449 0%, #034d6b 100%);
}

.pdf-viewer-iframe {
  width: 100%;
  height: calc(100vh - 200px);
  min-height: 600px;
  background-color: #525659;
}

.pdf-loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 600px;
  background-color: #f5f5f5;
}

/* Responsive */
@media (max-width: 768px) {
  .anexo-header-title {
    font-size: 0.938rem;
  }

  .pdf-viewer-iframe {
    height: 75vh;
    min-height: 500px;
  }
}
</style>